/**
 * VideoPlayerWithSubtitles
 *
 * 本组件只消费后端聚合后的 VideoDescriptor：
 * - 不直接读取本地 JSON 元数据文件
 * - 不推断文件存在性
 * - 不自己计算 hasOutput / hasJson
 * 所有业务判断统一使用 VideoDescriptor 的语义化字段。
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Clock, XCircle } from 'lucide-react';
import AppShell from './AppShell';
import './VideoPlayerWithSubtitles.css';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth } from '../utils/auth';
import { resolveAssetUrl } from '../utils/assetUrl';
import type { ApiResponse, VideoDescriptor } from '../types/api';
import { toRomaji } from 'wanakana';
import { textToIpaWords, IpaWord, lookupIpa } from '../utils/englishIpa';
import { useHlsPlayer } from '../hooks/useHlsPlayer';
import { useSubtitleControlState, type SubtitleViewMode } from '../hooks/useSubtitleControlState';
import { usePlayerEngine } from '../hooks/usePlayerEngine';
import { mergeRefs } from '../utils/mergeRefs';
import { getKuromojiManager } from '../utils/kuromojiManager';
import type { Tokenizer, Token } from 'kuromoji';
import { ProgressBar } from './player/ProgressBar';
import { ControlsLeft } from './player/ControlsLeft';
import { ControlsCenter } from './player/ControlsCenter';
import { ControlsRight } from './player/ControlsRight';

// 背景颜色数组（常量，移到组件外部以避免每次渲染时重新创建）
// 为了保证相邻词的颜色对比更明显，按“冷暖交替 + 饱和度差异”重新排列。
const BACKGROUND_COLORS = [
  'rgba(255, 107, 107, 0.1)',  // 红
  'rgba(133, 193, 226, 0.1)',  // 天蓝
  'rgba(248, 183, 57, 0.1)',   // 橙
  'rgba(78, 205, 196, 0.1)',   // 青绿
  'rgba(247, 220, 111, 0.1)',  // 黄
  'rgba(69, 183, 209, 0.1)',   // 蓝
  'rgba(255, 160, 122, 0.1)',  // 浅橙
  'rgba(82, 190, 128, 0.1)',   // 绿
  'rgba(187, 143, 206, 0.1)',  // 紫
  'rgba(152, 216, 200, 0.1)',  // 薄荷绿
];

interface Subtitle {
  start: number;
  end: number;
  text: string;
}

// 词级数据（统一视图层）：用于前端高亮
// 来自 display_words.json（视图层）或由 words.json 规范化而来（raw 模式）
interface DisplayWord {
  start: number;
  end: number;
  text: string;
  // 指回原始 words.json 中的 word 索引，保证可追溯（raw 模式下就是 [自身索引]）
  sourceWordIndices: number[];
}

interface DisplayWordsSegment {
  id: number;
  start: number;
  end: number;
  text: string;
  displayWords: DisplayWord[];
}

interface DisplayWordsJson {
  text: string;
  language: string;
  duration: number;
  segments: DisplayWordsSegment[];
}

type LanguageCode = string;

interface LanguageDisplaySettings {
  showFurigana?: boolean; // 仅日语用：是否显示假名
  showRomaji?: boolean;   // 仅日语用：是否显示罗马字
  showIpa?: boolean;      // 英语用：是否显示 IPA 音标
  // 将来可以扩展其他语言的设置
}

const VideoPlayerWithSubtitles: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // 从 location.state 获取初始标题和进度（类型安全）
  interface LocationState {
    initialTitle?: string;
    initialProgress?: number;
  }
  const locationState = location.state as LocationState | null;
  const initialTitleFromState = locationState?.initialTitle;
  const initialProgressFromState = locationState?.initialProgress;
  // 标题策略：
  // - 入口页传了 initialTitle：优先展示
  // - metadata 返回后有 descriptor.name：覆盖为 descriptor.name
  // - 都没有：保持空字符串，不再显示“未命名视频”
  const [title, setTitle] = useState<string>(initialTitleFromState || '');
  
  // 获取来源页面参数，用于返回时导航到正确的页面
  const fromPage = searchParams.get('from') || 'list'; // 默认为 list（首页）
  // 当 fromPage === 'collection' 时，附带合集 ID，返回时回到具体合集页
  const fromCollectionId = searchParams.get('collectionId') || '';
  // 由 CollectionDetail 透传过来的"合集页本身的返回路径"
  // 让 CollectionDetail 重新挂载时仍能回到最初来源
  const fromReturnTo = searchParams.get('returnTo') || '';
  const [availableVideos, setAvailableVideos] = useState<VideoDescriptor[]>([]);
  const [selectedVideoId, setSelectedVideoId] = useState<string>('');
  const [descriptor, setDescriptor] = useState<VideoDescriptor | null>(null);
  const [originalSubtitles, setOriginalSubtitles] = useState<Subtitle[]>([]);
  const [translatedSubtitles, setTranslatedSubtitles] = useState<Subtitle[]>([]);
  // 词级数据（统一形状）：根据 descriptor.wordHighlightMode 来源于 display_words.json 或 words.json
  //   - 'display'：直接是 display_words.json（视图层）
  //   - 'raw'    ：由 words.json 规范化得来（每个 word 自成一个 displayWord）
  const [displayWordData, setDisplayWordData] = useState<DisplayWordsJson | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(0);
  // 【建议6】时间显示文本节流状态：用于显示，更新频率 ≤10fps（约每100ms更新一次）
  const [displayTime, setDisplayTime] = useState<number>(0);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  // 用于防止移动端触摸和点击事件重复触发
  const touchHandledRef = useRef<boolean>(false);
  const [currentSubtitleIndex, setCurrentSubtitleIndex] = useState<number>(-1);
  const [playbackRate, setPlaybackRate] = useState<number>(1.0);
  const [isRepeatMode, setIsRepeatMode] = useState<boolean>(false);
  const [repeatSubtitleIndex, setRepeatSubtitleIndex] = useState<number>(-1);
  const [currentWordIndex, setCurrentWordIndex] = useState<{ segmentIndex: number; wordIndex: number } | null>(null);
  // 词级对齐开关：默认开启，用户可以手动关闭
  const [enableWordAlignment, setEnableWordAlignment] = useState<boolean>(true);
  // HLS 播放错误状态
  const [hlsError, setHlsError] = useState<Error | null>(null);

  // 性能优化：使用二分查找查找字幕索引（O(log N) 替代 O(N)）
  const findSubtitleIndexByTime = useCallback((
    originalSubtitles: Subtitle[],
    translatedSubtitles: Subtitle[],
    time: number
  ): number => {
    const maxLength = Math.max(originalSubtitles.length, translatedSubtitles.length);
    if (maxLength === 0) return -1;

    let left = 0;
    let right = maxLength - 1;
    let result = -1;

    // 允许一点点时间误差，避免 HLS seek 到 startTime 附近时被判定到上一句
    const EPS = 0.05; // 50ms 容忍度

    while (left <= right) {
      const mid = (left + right) >> 1;
      const original = originalSubtitles[mid];
      const translated = translatedSubtitles[mid];
      
      const start = (original && original.start !== undefined) ? original.start : 
                    (translated && translated.start !== undefined) ? translated.start : 0;
      const end = (original && original.end !== undefined) ? original.end : 
                  (translated && translated.end !== undefined) ? translated.end : 0;

      if (time < start - EPS) {
        right = mid - 1;
      } else if (time >= end + EPS) {
        left = mid + 1;
      } else {
        // 在 [start-EPS, end+EPS) 之间都认为命中当前字幕，避免边界抖动
        result = mid;
        break;
      }
    }

    // 如果没找到精确落点，保守返回 right（最后一个开始时间小于当前 time 的）
    // 如果 right < 0，说明时间在所有字幕之前，返回 0
    return result !== -1 ? result : Math.max(0, right);
  }, []);

  // 词级高亮推进状态：最后一次用于 UI 渲染的时间（仅用于 UI 节流，不影响状态机推进）

  // 判断某一段的词级数据是否仍与原文 VTT 一致
  // - 段落数量必须匹配（否则整体作废）
  // - 该段的文本必须与 VTT 对应段一致（去前后空白后比较）
  // 一旦不一致：表示用户已编辑 VTT，词级 JSON 已陈旧 → 该段不再走词级渲染/高亮
  const isSegmentWordDataValid = useCallback((segmentIndex: number): boolean => {
    if (!displayWordData?.segments) return false;
    if (segmentIndex < 0 || segmentIndex >= displayWordData.segments.length) return false;
    // 句数不一致 → 整体作废
    if (originalSubtitles.length > 0 && displayWordData.segments.length !== originalSubtitles.length) {
      return false;
    }
    const seg = displayWordData.segments[segmentIndex];
    const vttCue = originalSubtitles[segmentIndex];
    if (!seg || !vttCue) return false;
    return (seg.text || '').trim() === (vttCue.text || '').trim();
  }, [displayWordData, originalSubtitles]);

  // 获取某个段落的"有效词列表"
  // - 以 VTT 为准：若词级 JSON 与 VTT 不一致（数量或文本变更），该段返回空
  // - 上层 renderTextWithWords / advanceWordHighlight 在 words 为空时自动 fallback 到 VTT 原文，不做词级高亮
  const getEffectiveWordsForSegment = useCallback((
    segmentIndex: number
  ): { word: string; start: number; end: number; score?: number }[] => {
    if (!isSegmentWordDataValid(segmentIndex)) return [];
    const seg = displayWordData!.segments[segmentIndex];
    if (seg && Array.isArray(seg.displayWords) && seg.displayWords.length > 0) {
      return seg.displayWords.map(dw => ({
        word: dw.text,
        start: dw.start,
        end: dw.end,
      }));
    }
    return [];
  }, [displayWordData, isSegmentWordDataValid]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const subtitleContainerRef = useRef<HTMLDivElement>(null);
  // 当鼠标悬停在字幕区域时，暂停自动滚动，避免用户指向的字幕被频繁滚动导致闪烁
  const isSubtitleAreaHoveredRef = useRef<boolean>(false);
  // 使用 kuromoji Tokenizer 类型
  const kuromojiTokenizerRef = useRef<Tokenizer | null>(null);
  const [kuromojiReady, setKuromojiReady] = useState<boolean>(false);
  const romajiCacheRef = useRef<Record<number, string>>({});
  
  // 性能优化：缓存上一次用于 UI 更新的时间（词级状态机不依赖此值）
  const lastUiTimeRef = useRef<number>(0);
  const lastSubtitleIndexRef = useRef<number>(-1);
  const lastWordIndexRef = useRef<{ segmentIndex: number; wordIndex: number } | null>(null);
  // 点击字幕后的锁定索引：在锁定期间内，currentSubtitleIndex 不允许回退到该索引之前
  const lockedSubtitleIndexRef = useRef<number | null>(null);
  // 进度条拖动状态：用于区分拖动过程和拖动结束
  const isDraggingRef = useRef<boolean>(false);
  // 拖动开始时的播放状态：用于在拖动结束后恢复播放
  const wasPlayingBeforeDragRef = useRef<boolean>(false);
  // 进度条拖动时的本地值（半受控模式）
  const draggingValueRef = useRef<number | null>(null);
  // 控制区 DOM 引用：用于直接更新 CSS 变量，避免触发 React re-render
  const controlAreaRef = useRef<HTMLDivElement>(null);
  // 【建议6】时间显示文本节流：避免高频更新（≤10fps，约每100ms更新一次）
  const lastDisplayTimeUpdateRef = useRef<number>(0);
  const lastDisplayTimeRef = useRef<number>(0);
  // 浏览历史记录：用于防抖和避免频繁请求
  const lastRecordedProgressRef = useRef<number>(-1);
  const historyRecordTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasRecordedFirstPlayRef = useRef<boolean>(false);
  const lastVisibilityRecordRef = useRef<number>(0); // 最后一次visibilitychange记录的时间戳
  // 最近点击的字幕信息：用于在点击后短时间内优先使用点击的字幕索引
  const lastClickedSubtitleRef = useRef<{ index: number; time: number; timestamp: number } | null>(null);
  // 是否已经应用过"从浏览历史恢复进度"的逻辑，避免重复 seek
  const hasAppliedInitialProgressRef = useRef<boolean>(false);
  
  // 字幕显示模式
  const [subtitleViewMode, setSubtitleViewMode] = useState<SubtitleViewMode>('both');
  
  // 播放器引擎：统一封装所有 video 操作
  const playerActions = usePlayerEngine({ videoRef });
  
  // 【建议4】使用字幕控制状态 hook
  const subtitleControl = useSubtitleControlState({
    currentSubtitleIndex,
    originalSubtitles,
    translatedSubtitles,
    wordSegments: displayWordData?.segments ?? null,
    enableWordAlignment,
    hasWordAlignment: descriptor?.capabilities?.hasWordAlignment ?? false,
    subtitleViewMode,
    setSubtitleViewMode,
    isRepeatMode,
    setIsRepeatMode,
    repeatSubtitleIndex,
    setRepeatSubtitleIndex,
    setCurrentWordIndex,
    playerActions,
  });
  
  // 按语言存显示设置
  const [displaySettings, setDisplaySettings] = useState<Record<LanguageCode, LanguageDisplaySettings>>({});
  const [showDisplaySettingsPanel, setShowDisplaySettingsPanel] = useState(false);
  const [isSpeedSelectorOpen, setIsSpeedSelectorOpen] = useState(false);
  // 字幕大小缩放比例（1.0 = 100%，范围：0.8 - 1.5）
  const [subtitleSizeScale, setSubtitleSizeScale] = useState<number>(() => {
    const saved = localStorage.getItem('subtitleSizeScale');
    return saved ? Math.max(0.8, Math.min(1.5, parseFloat(saved))) : 1.0;
  });

  // 点击外部区域关闭显示设置面板
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const displaySettingsPanel = document.querySelector('.display-settings-panel');
      const displaySettingsToggle = document.querySelector('.display-settings-toggle');

      // 如果点击的不是面板内部和按钮本身，则关闭面板
      if (showDisplaySettingsPanel &&
          displaySettingsPanel &&
          !displaySettingsPanel.contains(target) &&
          displaySettingsToggle &&
          !displaySettingsToggle.contains(target)) {
        setShowDisplaySettingsPanel(false);
      }
    };

    if (showDisplaySettingsPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDisplaySettingsPanel]);

  // 应用字幕大小缩放（通过CSS变量）
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--subtitle-size-scale', subtitleSizeScale.toString());
    localStorage.setItem('subtitleSizeScale', subtitleSizeScale.toString());
  }, [subtitleSizeScale]);

  // 调整字幕大小的函数（使用 useCallback 优化性能）
  const adjustSubtitleSize = useCallback((delta: number) => {
    setSubtitleSizeScale(prev => {
      const newScale = Math.max(0.8, Math.min(1.5, prev + delta));
      return newScale;
    });
  }, []);

  // 重置字幕大小（使用 useCallback 优化性能）
  const resetSubtitleSize = useCallback(() => {
    setSubtitleSizeScale(1.0);
  }, []);

  // 从 descriptor 派生字幕 URL（使用共享的 resolveAssetUrl 工具解析）
  const originalSubtitleUrl = resolveAssetUrl(descriptor?.assetUrls?.subtitle_original?.url);
  const translatedSubtitleUrl = resolveAssetUrl(descriptor?.assetUrls?.subtitle_translated?.url);
  
  // 计算 HLS URL
  const hlsUrl = descriptor?.hlsStatus === 'ready' && descriptor?.assetUrls?.hls?.url
    ? resolveAssetUrl(descriptor.assetUrls.hls.url)
    : undefined;

  // 使用 HLS 播放器 Hook（不再接收 videoRef）
  const { videoCallbackRef, hlsRef } = useHlsPlayer({
    enabled: descriptor?.hlsStatus === 'ready' && !!hlsUrl,
    hlsUrl,
    getAuthHeaders: () => {
      const headers = getAuthHeaders();
      // 将 HeadersInit 转换为 Record<string, string>
      if (headers instanceof Headers) {
        const result: Record<string, string> = {};
        headers.forEach((value, key) => {
          result[key] = value;
        });
        return result;
      } else if (Array.isArray(headers)) {
        return Object.fromEntries(headers) as Record<string, string>;
      } else {
        return headers as Record<string, string>;
      }
    },
    onFatalError: (error) => {
      console.error('HLS 播放失败:', error);
      setHlsError(error);
    },
    onError: (error, details) => {
      console.warn('HLS 播放错误（已恢复）:', error, details);
    },
  });

  // 合并 refs（videoRef 用于其他逻辑，videoCallbackRef 用于 HLS）
  const mergedVideoRef = mergeRefs(videoRef, videoCallbackRef);

  // 优先使用入口页面传过来的标题；metadata 加载完再用 descriptor.name 覆盖
  // 如果两边都没有标题，则保持空，不显示“未命名视频”
  const displayTitle = descriptor
    ? (descriptor.name || title || '')
    : (title || '');

  // 使用全局 Kuromoji 管理器（避免每次进入页面都重新加载字典文件）
  useEffect(() => {
    const manager = getKuromojiManager();

    // 如果已经就绪，直接使用
    if (manager.isReady()) {
      kuromojiTokenizerRef.current = manager.getTokenizer();
      setKuromojiReady(true);
      return;
    }

    // 如果正在初始化或未初始化，等待完成
    manager.waitForReady().then((ready: boolean) => {
      if (ready) {
        kuromojiTokenizerRef.current = manager.getTokenizer();
        setKuromojiReady(true);
      }
      // 如果初始化失败，kuromojiReady 保持 false，应用继续运行（不使用假名功能）
    });
  }, []);

  // 将片假名转换为平假名
  const katakanaToHiragana = useCallback((text: string): string => {
    return text.split('').map((char) => {
      const code = char.charCodeAt(0);
      // 片假名范围：\u30A1-\u30F6 (カタカナ)
      // 平假名范围：\u3041-\u3096 (ひらがな)
      // 转换：片假名 = 平假名 + 0x60
      if (code >= 0x30A1 && code <= 0x30F6) {
        return String.fromCharCode(code - 0x60);
      }
      return char;
    }).join('');
  }, []);

  // 将单个词转换为包含假名、原文、romaji的三层结构
  const convertWordToThreeLayer = useCallback((surface: string, reading: string, showFurigana: boolean, showRomaji: boolean, baseStyle?: React.CSSProperties): React.ReactNode => {
    // 如果没有 reading，使用 surface（可能是纯假名）
    const effectiveReading = reading || surface;
    
    // 将片假名转换为平假名
    const hiragana = katakanaToHiragana(effectiveReading);
    
    // 转换为romaji
    let romajiText = '';
    if (showRomaji && hiragana) {
      romajiText = toRomaji(hiragana);
    }
    
    // 检查是否包含汉字
    const hasKanji = /[\u4e00-\u9faf]/.test(surface);
    
    // 只要这一块里有汉字且开启了假名，就一定显示假名
    // （不再要求 hiragana 和 surface 必须不同）
    const shouldShowFurigana = showFurigana && hasKanji && !!hiragana;
    
    // 所有词都使用统一的三层结构，确保原文始终在中间层显示。
    // 为了让“词级高亮”的框只包住原文行，而不包住上方假名，
    // 这里在 ruby-base 外再包一层 ruby-base-wrap，边框/背景可以作用在 wrapper 上。
    return (
      <span className="jp-word">
        {/* 始终渲染 ruby-top，但不显示假名时使用 visibility: hidden 保持空间，避免重排 */}
        <span className="ruby-top" style={shouldShowFurigana ? {} : { visibility: 'hidden' }}>
          {hiragana}
        </span>
        <span className="ruby-base-wrap" style={baseStyle}>
          <span className="ruby-base">{surface}</span>
        </span>
        {/* 始终渲染 ruby-bottom，但不显示罗马字时使用 visibility: hidden 保持空间，避免重排 */}
        <span className="ruby-bottom" style={showRomaji && romajiText ? {} : { visibility: 'hidden' }}>
          {romajiText || '\u00A0'} {/* 使用不间断空格作为占位符 */}
        </span>
      </span>
    );
  }, [katakanaToHiragana]);

  // 将日语文本转换为带假名和romaji的三层结构
  // baseStyle 仅作用于中间那层原文（ruby-base），用于高亮/描边，不影响假名和罗马字
  const convertToRuby = useCallback((
    text: string,
    showFurigana: boolean,
    showRomaji: boolean,
    baseStyle?: React.CSSProperties
  ): React.ReactNode => {
    if (!kuromojiReady || !kuromojiTokenizerRef.current) {
      return text; // 如果 Kuromoji 未初始化，返回原始文本
    }

    try {
      const tokenizer = kuromojiTokenizerRef.current;
      const tokens = tokenizer.tokenize(text);
      
      const result: React.ReactNode[] = [];
      tokens.forEach((token: Token, index: number) => {
        const surface = token.surface_form;
        let reading = token.reading || token.pronunciation || '';
        
        // 如果没有 reading，使用 surface_form（可能是纯假名）
        if (!reading) {
          reading = surface;
        }
        
        result.push(
          <React.Fragment key={index}>
            {convertWordToThreeLayer(surface, reading, showFurigana, showRomaji, baseStyle)}
          </React.Fragment>
        );
      });
      
      return <>{result}</>;
    } catch (error) {
      return text; // 出错时返回原始文本
    }
  }, [kuromojiReady, convertWordToThreeLayer]);

  // 将日语文本转换为罗马字（romaji）
  const textToRomaji = useCallback((text: string): string => {
    if (!kuromojiReady || !kuromojiTokenizerRef.current) {
      return ''; // 如果 Kuromoji 未初始化，返回空字符串
    }

    try {
      const tokenizer = kuromojiTokenizerRef.current;
      const tokens = tokenizer.tokenize(text);
      
      const romajiParts: string[] = [];
      tokens.forEach((token: Token) => {
        // 获取假名（reading 或 pronunciation）
        let reading = token.reading || token.pronunciation || '';
        
        // 如果没有 reading，使用 surface_form（可能是纯假名）
        if (!reading) {
          reading = token.surface_form;
        }
        
        // 将片假名转换为平假名
        reading = katakanaToHiragana(reading);
        
        // 使用 wanakana 将假名转换为 romaji
        if (reading) {
          const romaji = toRomaji(reading);
          if (romaji) {
            romajiParts.push(romaji);
          }
        }
      });
      
      // 返回以空格分隔的 romaji 字符串
      return romajiParts.join(' ');
    } catch (error) {
      return ''; // 出错时返回空字符串
    }
  }, [kuromojiReady, katakanaToHiragana]);

  // 解析 WebVTT 文件
  const parseVTT = (vttText: string): Subtitle[] => {
    const subtitles: Subtitle[] = [];
    const lines = vttText.split('\n');
    let currentSubtitle: Subtitle | null = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // 跳过 WEBVTT 头部、空行和注释
      if (line === 'WEBVTT' || line === '' || line.startsWith('NOTE') || line.startsWith('STYLE')) {
        if (line === '' && currentSubtitle && currentSubtitle.text) {
          subtitles.push(currentSubtitle);
          currentSubtitle = null;
        }
        continue;
      }

      // 匹配时间轴: 00:00:00.000 --> 00:00:05.500
      const timeRegex = /^(\d{2}):(\d{2}):(\d{2})\.(\d{3})\s+-->\s+(\d{2}):(\d{2}):(\d{2})\.(\d{3})/;
      const timeMatch = line.match(timeRegex);

      if (timeMatch) {
        if (currentSubtitle && currentSubtitle.text) {
          subtitles.push(currentSubtitle);
        }

        const startTime =
          parseInt(timeMatch[1]) * 3600 +
          parseInt(timeMatch[2]) * 60 +
          parseInt(timeMatch[3]) +
          parseInt(timeMatch[4]) / 1000;

        const endTime =
          parseInt(timeMatch[5]) * 3600 +
          parseInt(timeMatch[6]) * 60 +
          parseInt(timeMatch[7]) +
          parseInt(timeMatch[8]) / 1000;

        currentSubtitle = {
          start: startTime,
          end: endTime,
          text: '',
        };
      } else if (currentSubtitle && line) {
        if (currentSubtitle.text) {
          currentSubtitle.text += ' ' + line;
        } else {
          currentSubtitle.text = line;
        }
      }
    }

    if (currentSubtitle && currentSubtitle.text) {
      subtitles.push(currentSubtitle);
    }

    return subtitles;
  };

  // 从 URL 加载 VTT 文件
  const loadVTTFromUrl = useCallback(async (url: string): Promise<Subtitle[]> => {
    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to load VTT file: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return parseVTT(text);
  }, []);

  // 加载视频列表（用于播放器左上角下拉和默认选中）
  // 使用 ref 防止重复请求（React.StrictMode 在开发环境会渲染两次）
  const videoListLoadingRef = useRef<boolean>(false);
  const videoListAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    interface VideoListResponse {
      list: VideoDescriptor[];
      page: number;
      pageSize: number;
      hasMore: boolean;
    }

    const loadVideoList = async () => {
      // 防止重复请求
      if (videoListLoadingRef.current) {
        return;
      }

      // 取消之前的请求（如果存在）
      if (videoListAbortControllerRef.current) {
        videoListAbortControllerRef.current.abort();
      }

      // 创建新的 AbortController
      const abortController = new AbortController();
      videoListAbortControllerRef.current = abortController;
      videoListLoadingRef.current = true;

      try {
        // 播放器只需要一页可选视频做"来源列表"，这里固定取第一页即可
        // 使用与其他列表一致的 pageSize（20），避免一次性加载过多数据
        const VIDEO_LIST_PAGE_SIZE = 20;
        const response = await fetch(`${API_BASE_URL}/video-list?page=1&pageSize=${VIDEO_LIST_PAGE_SIZE}`, {
          headers: getAuthHeaders(),
          signal: abortController.signal,
        });
        if (response.status === 401) {
          clearAuth();
          navigate('/login');
          return;
        }
        const resp: ApiResponse<VideoListResponse> = await response.json();
        if (!resp.success || !resp.data) {
          throw new Error(resp.error || '获取视频列表失败');
        }
        const list = resp.data.list || [];
        
        // 【边界 1】：只使用 playable 字段过滤可播放视频
        const playable = list.filter(v => v.playable);
        setAvailableVideos(playable);
        
        // 优先使用 URL 参数中的 videoId
        const videoIdFromUrl = searchParams.get('video');
        if (videoIdFromUrl) {
          const decodedVideoId = decodeURIComponent(videoIdFromUrl);
          // 检查视频是否在可播放列表中
          const videoExists = playable.some(v => v.videoId === decodedVideoId);
          if (videoExists) {
            setSelectedVideoId(decodedVideoId);
          } else {
            // URL 中指定的 videoId 不在列表中，但仍然尝试加载（可能是私有视频或权限问题）
            // 直接设置 videoId，让后续的 loadVideoMetadata 尝试加载
            // 如果加载失败，会在 loadVideoMetadata 中显示错误信息
            console.warn(`URL 中的 videoId "${decodedVideoId}" 不在可播放列表中，尝试直接加载...`);
            setSelectedVideoId(decodedVideoId);
          }
        } else if (playable.length > 0 && playable[0].videoId) {
          // 没有 URL 参数，使用列表中的第一个视频
          setSelectedVideoId(playable[0].videoId);
        }
      } catch (error) {
        // 如果是取消请求，不显示错误
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
      } finally {
        // 清理加载状态
        if (abortController === videoListAbortControllerRef.current) {
          videoListLoadingRef.current = false;
          videoListAbortControllerRef.current = null;
        }
      }
    };
    loadVideoList();

    // 清理函数：取消进行中的请求
    return () => {
      if (videoListAbortControllerRef.current) {
        videoListAbortControllerRef.current.abort();
        videoListAbortControllerRef.current = null;
      }
      videoListLoadingRef.current = false;
    };
  }, [searchParams]);

  // 清理浏览历史记录定时器（组件卸载时）
  useEffect(() => {
    return () => {
      // 清理浏览历史记录定时器
      if (historyRecordTimeoutRef.current) {
        clearTimeout(historyRecordTimeoutRef.current);
        historyRecordTimeoutRef.current = null;
      }
    };
  }, []);

  // 加载单个视频的 metadata
  useEffect(() => {
    const loadVideoMetadata = async () => {
      if (!selectedVideoId) return;

      // 重置first play状态，为新视频做准备
      hasRecordedFirstPlayRef.current = false;
      // 重置visibilitychange记录时间戳
      lastVisibilityRecordRef.current = 0;

      // 先清除旧的状态，避免切换视频时显示旧字幕
      setDescriptor(null);
      setOriginalSubtitles([]);
      setTranslatedSubtitles([]);
      setCurrentSubtitleIndex(-1);
      setCurrentWordIndex(null);
      setCurrentTime(0);
      setDisplayTime(0);
      lastDisplayTimeUpdateRef.current = 0;
      lastDisplayTimeRef.current = 0;
      // 切换视频时，如果新视频没有词级对齐数据，自动关闭词级对齐
      // 注意：如果新视频有词级对齐数据，保持用户之前的选择（如果之前是开启的，继续开启）
      // 清除 romaji 缓存
      romajiCacheRef.current = {};
      // 清除性能优化相关的 ref 缓存
      lastUiTimeRef.current = 0;
      // 重置拖动状态
      isDraggingRef.current = false;
      draggingValueRef.current = null;
      // 重置进度条视觉
      if (controlAreaRef.current) {
        controlAreaRef.current.style.setProperty('--progress', '0%');
      }
      lastSubtitleIndexRef.current = -1;
      lastWordIndexRef.current = null;
      // 重置初始进度应用标志，允许新视频恢复进度
      hasAppliedInitialProgressRef.current = false;


      try {
        const response = await fetch(`${API_BASE_URL}/video/metadata?videoId=${encodeURIComponent(selectedVideoId)}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          if (response.status === 401) {
            clearAuth();
            navigate('/login');
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const resp: ApiResponse<VideoDescriptor> = await response.json();
        if (!resp.success || !resp.data) {
          throw new Error(resp.error || '获取视频元数据失败');
        }
        
        const descriptor = resp.data;
        setDescriptor(descriptor);
        // 如果之前没有本地标题，则用后端返回的名称作为标题；没有就保持空
        setTitle(prev => prev || descriptor.name || '');

        // 【新逻辑】：基于 hlsStatus 判断是否可以播放
        if (descriptor.hlsStatus !== 'ready') {
          if (descriptor.hlsStatus === 'pending') {
            return; // 不抛出错误，显示等待状态
          } else if (descriptor.hlsStatus === 'error') {
            throw new Error('HLS 生成失败，请重新上传视频');
          } else {
            // hlsStatus 为 undefined 或其他值，视为 pending
            return;
          }
        }

        // 检查 HLS URL
        const resolvedHlsUrl = descriptor.assetUrls?.hls?.url
          ? resolveAssetUrl(descriptor.assetUrls.hls.url)
          : undefined;

        if (!resolvedHlsUrl) {
          throw new Error('HLS 播放地址不存在');
        }

        // HLS 模式下不需要 Blob URL，useHlsPlayer Hook 会处理播放逻辑

        // 【边界 1】：判断"有字幕"的条件
        // 使用语义化字段 capabilities，而不是直接检查 assetUrls
        const hasOriginalSubtitle = descriptor.capabilities.hasOriginalSubtitles;
        const hasTranslatedSubtitle = descriptor.capabilities.hasTranslatedSubtitles;

        // 解析字幕 URL（使用 resolveAssetUrl 确保正确的 URL）
        const resolvedOriginalSubtitleUrl = resolveAssetUrl(descriptor.assetUrls?.subtitle_original?.url);
        const resolvedTranslatedSubtitleUrl = resolveAssetUrl(descriptor.assetUrls?.subtitle_translated?.url);

        // 加载原文字幕
        if (hasOriginalSubtitle && resolvedOriginalSubtitleUrl) {
          const subtitles = await loadVTTFromUrl(resolvedOriginalSubtitleUrl);
          setOriginalSubtitles(subtitles);
        } else {
          throw new Error('原文字幕文件未找到');
        }

        // 加载翻译字幕
        if (hasTranslatedSubtitle && resolvedTranslatedSubtitleUrl) {
          const subtitles = await loadVTTFromUrl(resolvedTranslatedSubtitleUrl);
          setTranslatedSubtitles(subtitles);
        } else {
          throw new Error('翻译字幕文件未找到');
        }

        // 设置默认选中的字幕索引（如果不是从浏览历史来的话）
        const isFromHistoryWithProgress =
          fromPage === 'history' &&
          typeof initialProgressFromState === 'number' &&
          initialProgressFromState > 0;

        if (!isFromHistoryWithProgress && (hasOriginalSubtitle || hasTranslatedSubtitle)) {
          // 设置当前字幕索引为第 0 行
          setCurrentSubtitleIndex(0);
          lastSubtitleIndexRef.current = 0;
        }

        // 加载词级对齐 JSON：根据 wordHighlightMode 选择 display_words.json 或 words.json
        //   'display'（默认）：使用 display_words.json（合并/修复后的视图层）
        //   'raw'           ：使用 words.json（WhisperX 原始词级，不做合并）
        // 不论选哪个，都规范化为统一的 DisplayWordsJson 形状（raw 模式下每个 word 自成一个 displayWord）
        const mode: 'raw' | 'display' = descriptor.wordHighlightMode === 'raw' ? 'raw' : 'display';
        const rawWordsAssetUrl = mode === 'raw'
          ? descriptor.assetUrls?.words?.url
          : descriptor.assetUrls?.display_words?.url;
        const resolvedWordsUrl = rawWordsAssetUrl ? resolveAssetUrl(rawWordsAssetUrl) : undefined;
        // 命中 raw 模式但 words.json 缺失，则尝试退化到 display_words.json；反之亦然
        const fallbackUrl = mode === 'raw'
          ? descriptor.assetUrls?.display_words?.url
          : descriptor.assetUrls?.words?.url;
        const resolvedFallbackUrl = fallbackUrl ? resolveAssetUrl(fallbackUrl) : undefined;
        const finalWordsUrl = resolvedWordsUrl ?? resolvedFallbackUrl;
        const finalMode: 'raw' | 'display' =
          resolvedWordsUrl ? mode : (resolvedFallbackUrl ? (mode === 'raw' ? 'display' : 'raw') : mode);

        if (finalWordsUrl) {
          try {
            const wordsResp = await fetch(finalWordsUrl, { headers: getAuthHeaders() });
            if (wordsResp.ok) {
              const raw = await wordsResp.json();
              const normalized: DisplayWordsJson = finalMode === 'raw'
                ? {
                    text: typeof raw.text === 'string' ? raw.text : '',
                    language: typeof raw.language === 'string' ? raw.language : '',
                    duration: typeof raw.duration === 'number' ? raw.duration : 0,
                    segments: Array.isArray(raw.segments)
                      ? raw.segments.map((seg: any, segIdx: number): DisplayWordsSegment => {
                          const words = Array.isArray(seg?.words) ? seg.words : [];
                          return {
                            id: typeof seg?.id === 'number' ? seg.id : segIdx,
                            start: typeof seg?.start === 'number' ? seg.start : 0,
                            end: typeof seg?.end === 'number' ? seg.end : 0,
                            text: typeof seg?.text === 'string' ? seg.text : '',
                            displayWords: words.map((w: any, i: number): DisplayWord => ({
                              text: typeof w?.word === 'string' ? w.word : '',
                              start: typeof w?.start === 'number' ? w.start : 0,
                              end: typeof w?.end === 'number' ? w.end : 0,
                              sourceWordIndices: [i],
                            })),
                          };
                        })
                      : [],
                  }
                : (raw as DisplayWordsJson);
              setDisplayWordData(normalized);

              // 如果有词级数据，并且是"首次观看"（没有历史进度），默认选中第一行和第一个词
              const isFromHistoryWithProgress =
                fromPage === 'history' &&
                typeof initialProgressFromState === 'number' &&
                initialProgressFromState > 0;

              if (
                enableWordAlignment &&
                !isFromHistoryWithProgress &&
                normalized.segments &&
                normalized.segments.length > 0
              ) {
                const firstSegment = normalized.segments[0]!;
                setCurrentSubtitleIndex(0);
                lastSubtitleIndexRef.current = 0;

                if (firstSegment.displayWords && firstSegment.displayWords.length > 0) {
                  const newWordIndex = { segmentIndex: 0, wordIndex: 0 };
                  setCurrentWordIndex(newWordIndex);
                  lastWordIndexRef.current = newWordIndex;
                } else {
                  setCurrentWordIndex(null);
                  lastWordIndexRef.current = null;
                }
              }
            } else {
              console.warn(
                `${finalMode === 'raw' ? 'words.json' : 'display_words.json'} not available, word-level view disabled:`,
                wordsResp.status
              );
              setDisplayWordData(null);
            }
          } catch (error) {
            console.warn('Failed to load word-level data:', error);
            setDisplayWordData(null);
          }
        } else {
          setDisplayWordData(null);
        }

        // 如果视频没有词级对齐数据（hasWordAlignment 为 false），自动关闭词级对齐开关
        if (!descriptor.capabilities?.hasWordAlignment) {
          setEnableWordAlignment(false);
        }

      } catch (error) {
        setDescriptor(null);
        setOriginalSubtitles([]);
        setTranslatedSubtitles([]);
        setDisplayWordData(null);
      }
    };
    loadVideoMetadata();
  }, [selectedVideoId, loadVTTFromUrl]);

  // 执行实际的API调用
  const performRecordHistory = useCallback(async (videoId: string, progress: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/history`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: videoId,
          progress: progress,
        }),
      });

      if (response.ok) {
        lastRecordedProgressRef.current = progress;
      }
    } catch (error) {
      // 静默失败，避免影响用户体验
      console.warn('记录观看历史失败:', error);
    }
  }, []);

  // 记录浏览历史（防抖：每5秒或进度变化超过5%时记录一次）
  const recordHistory = useCallback(async (videoId: string, progress: number, immediate: boolean = false) => {
    // 对于立即记录（first play），跳过所有防抖逻辑，直接执行
    if (immediate) {
      await performRecordHistory(videoId, progress);
      return;
    }

    // 防抖：如果进度变化小于5%，且距离上次记录不足5秒，则跳过
    const progressDiff = Math.abs(progress - lastRecordedProgressRef.current);
    if (progressDiff < 5 && lastRecordedProgressRef.current >= 0) {
      return;
    }

    // 清除之前的定时器
    if (historyRecordTimeoutRef.current) {
      clearTimeout(historyRecordTimeoutRef.current);
    }

    // 设置新的定时器（防抖：延迟1秒记录，避免频繁请求）
    historyRecordTimeoutRef.current = setTimeout(() => {
      performRecordHistory(videoId, progress);
    }, 1000);
  }, []);

  // 【规则1】退出时强制 flush：保证进度永远不会丢
  useEffect(() => {
    const handleVisibilityChange = () => {
      // 当页面变为隐藏状态时，立即记录当前进度（30秒去重）
      if (document.visibilityState === 'hidden') {
        const now = Date.now();
        // 5秒内不重复记录，保护用户体验 + 足够防重
        if (now - lastVisibilityRecordRef.current > 5000) {
          // 强制执行所有待处理的记录
          if (historyRecordTimeoutRef.current) {
            clearTimeout(historyRecordTimeoutRef.current);
            historyRecordTimeoutRef.current = null;
          }

          // 记录当前进度
          if (selectedVideoId && videoRef.current && videoRef.current.duration > 0) {
            const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
            navigator.sendBeacon(`${API_BASE_URL}/user/history`, JSON.stringify({
              videoId: selectedVideoId,
              progress: currentProgress,
            }));

            // 更新最后记录时间戳
            lastVisibilityRecordRef.current = now;
          }
        }
      }
    };

    // 只监听页面可见性变化（核心事件，覆盖所有场景）
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // 组件卸载时最后一次记录（兜底）
      if (selectedVideoId && videoRef.current && videoRef.current.duration > 0) {
        const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
        navigator.sendBeacon(`${API_BASE_URL}/user/history`, JSON.stringify({
          videoId: selectedVideoId,
          progress: currentProgress,
        }));
      }
    };
  }, [selectedVideoId]);

  // 更新进度条视觉（直接更新 CSS 变量，不触发 React re-render）
  const updateProgressBarVisual = useCallback((time: number, duration: number) => {
    if (!controlAreaRef.current || duration <= 0) return;
    const percent = (time / duration) * 100;
    controlAreaRef.current.style.setProperty('--progress', `${percent}%`);
  }, []);

  // 词级高亮状态机推进：由 requestAnimationFrame 驱动
  const advanceWordHighlight = useCallback((time: number) => {
    if (!enableWordAlignment || !displayWordData || displayWordData.segments.length === 0) {
      return;
    }

    // 段落识别永远以原文句级 VTT 为准；词级 JSON 仅提供"段内的词级时间"。
    // 这样即便 VTT 与词级 JSON 在数量/边界上不一致，UI 切句也永远跟 VTT 一致。
    let matchedSegmentIdx = -1;

    if (isRepeatMode && repeatSubtitleIndex >= 0) {
      matchedSegmentIdx = repeatSubtitleIndex;
    } else {
      // 检查是否有最近点击的字幕（在点击后 500ms 内，且时间接近点击的时间）
      const clickedSubtitle = lastClickedSubtitleRef.current;
      if (
        clickedSubtitle &&
        Date.now() - clickedSubtitle.timestamp < 500 &&
        Math.abs(time - clickedSubtitle.time) < 0.5
      ) {
        // 优先使用点击的字幕索引
        matchedSegmentIdx = clickedSubtitle.index;
        lastClickedSubtitleRef.current = null;
      } else if (originalSubtitles.length > 0 || translatedSubtitles.length > 0) {
        // 使用 VTT 句级时间定位当前段（与 handleTimeUpdate 保持一致）
        matchedSegmentIdx = findSubtitleIndexByTime(originalSubtitles, translatedSubtitles, time);
      }
    }

    if (matchedSegmentIdx < 0) {
      return;
    }

    // 段内的词级数据取自当前生效的词级 JSON（按索引 1:1 对应）。
    // 若 VTT 段数多于词级 JSON，越界段会返回空，相当于该段不做词级高亮。
    const effectiveWords = getEffectiveWordsForSegment(matchedSegmentIdx);
    if (!effectiveWords || effectiveWords.length === 0) {
      return;
    }

    // === 帧级单步推进：一帧最多前进一个词 ===
    // 如果当前没有词索引，或段落发生变化，从该段落的第一个词开始
    let wordIdx =
      lastWordIndexRef.current &&
      lastWordIndexRef.current.segmentIndex === matchedSegmentIdx
        ? lastWordIndexRef.current.wordIndex
        : 0;

    // 防御：确保索引在范围内
    if (wordIdx < 0 || wordIdx >= effectiveWords.length) {
      wordIdx = 0;
    }

    // 如果时间倒退（例如用户拖动进度条往前），从头重新推进
    if (time < effectiveWords[wordIdx].start) {
      wordIdx = 0;
    }

    // 使用 next.start 判断，而不是 current.end；一帧最多推进一个词
    if (
      wordIdx + 1 < effectiveWords.length &&
      time >= effectiveWords[wordIdx + 1].start
    ) {
      wordIdx++;
    }

    const newWordIndex = { segmentIndex: matchedSegmentIdx, wordIndex: wordIdx };

    // 只有在词索引真正发生变化时才更新 state，避免不必要的重渲染
    if (
      lastWordIndexRef.current === null ||
      lastWordIndexRef.current.segmentIndex !== newWordIndex.segmentIndex ||
      lastWordIndexRef.current.wordIndex !== newWordIndex.wordIndex
    ) {
      lastWordIndexRef.current = newWordIndex;
      setCurrentWordIndex(newWordIndex);
    }
  }, [displayWordData, enableWordAlignment, getEffectiveWordsForSegment, isRepeatMode, repeatSubtitleIndex, originalSubtitles, translatedSubtitles, findSubtitleIndexByTime]);

  // 请求动画帧驱动的词级高亮循环
  const rafIdRef = useRef<number | null>(null);
  const tickWordHighlight = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      rafIdRef.current = null;
      return;
    }
    if (video.paused || video.ended) {
      rafIdRef.current = null;
      return;
    }

    const time = video.currentTime;
    advanceWordHighlight(time);

    rafIdRef.current = requestAnimationFrame(tickWordHighlight);
  }, [advanceWordHighlight]);

  // 视频时间更新处理：仅负责 UI（进度条 / 时间显示 / 段落索引）
  const handleTimeUpdate = useCallback(() => {
    if (!videoRef.current) return;
    if (originalSubtitles.length === 0 || translatedSubtitles.length === 0) return;

    // 【P1】使用 player actions 获取时间，保持一致性
    const time = playerActions.getCurrentTime();
    const duration = playerActions.getDuration() || videoDuration;
    
    // 如果正在拖动，不更新进度条（使用拖动时的本地值）
    if (isDraggingRef.current) {
      return;
    }
    
    // 性能优化节流仅作用于 UI（进度条 / displayTime），不阻断词级状态机推进
    // 0.05s 阈值：既能保证进度条更新流畅，又能减少不必要的状态更新
    const uiTimeDiff = Math.abs(time - lastUiTimeRef.current);
    if (uiTimeDiff >= 0.05) {
      lastUiTimeRef.current = time;
      
      // 【建议1】使用 requestAnimationFrame 直接更新 CSS 变量，避免触发 React re-render
      requestAnimationFrame(() => {
        updateProgressBarVisual(time, duration);
      });
      
      // 只在变化时更新 currentTime（使用函数式更新进一步优化）
      // 注意：currentTime 仍然需要更新，因为字幕逻辑依赖它，但进度条视觉已剥离
      setCurrentTime(prev => {
        if (Math.abs(prev - time) < 0.01) return prev;
        return time;
      });
    }

    // 【建议6】时间显示文本节流更新（≤10fps，约每100ms更新一次）
    const now = Date.now();
    if (now - lastDisplayTimeUpdateRef.current >= 100 || Math.abs(time - lastDisplayTimeRef.current) >= 0.5) {
      lastDisplayTimeUpdateRef.current = now;
      lastDisplayTimeRef.current = time;
      setDisplayTime(time);
    }

    // 如果刚刚点击过某一行字幕，则在一小段时间内“锁定”在这行，
    // 优先使用点击的索引，避免因为 HLS seek 误差或边界抖动短暂跳回上一行。
    const clickedSubtitle = lastClickedSubtitleRef.current;
    if (clickedSubtitle) {
      const clickedIndex = clickedSubtitle.index;
      const clickedOriginal = originalSubtitles[clickedIndex];
      const clickedTranslated = translatedSubtitles[clickedIndex];
      const clickedStart = (clickedOriginal && clickedOriginal.start !== undefined)
        ? clickedOriginal.start
        : (clickedTranslated && clickedTranslated.start !== undefined)
          ? clickedTranslated.start
          : 0;
      const clickedEnd = (clickedOriginal && clickedOriginal.end !== undefined)
        ? clickedOriginal.end
        : (clickedTranslated && clickedTranslated.end !== undefined)
          ? clickedTranslated.end
          : 0;

      // 允许一个很小的误差范围，避免因为浮点误差导致刚好落在边界外
      const EPS_CLICK = 0.05; // 50ms
      const nowTs = Date.now();
      const MAX_LOCK_MS = 1500; // 点击后 1.5 秒内强制锁定到点击的那一行

      const withinLockWindow = nowTs - clickedSubtitle.timestamp <= MAX_LOCK_MS;
      const withinTimeRange = time >= clickedStart - EPS_CLICK && time < clickedEnd + EPS_CLICK;

      if (withinLockWindow || withinTimeRange) {
        // 在锁定时间窗口内，或者时间已经进入被点击字幕的范围：
        // 一律强制使用点击的索引，并跳过后续自动推断逻辑，避免“瞬间跳回上一行”的闪烁。
        if (clickedIndex !== lastSubtitleIndexRef.current) {
          lastSubtitleIndexRef.current = clickedIndex;
          setCurrentSubtitleIndex(clickedIndex);
          // 字幕索引变化时，清空词索引（由 rAF 循环重新推进）
          lastWordIndexRef.current = null;
          setCurrentWordIndex(null);
        }

        // 如果已经明显超过这一行的结束时间很多（超出误差 + 显著偏移），
        // 则认为锁定失效，允许下一帧回到自动逻辑。
        if (time >= clickedEnd + 0.5) {
          lastClickedSubtitleRef.current = null;
        }

        // 记录浏览历史（仅在视频时长有效时）
        if (videoDuration > 0 && selectedVideoId) {
          const progress = (time / videoDuration) * 100;
          recordHistory(selectedVideoId, progress);
        }
        return;
      }
      // 锁定时间窗口已过且时间也不在该字幕范围内，清除点击锁定，让后续逻辑按正常规则计算
      lastClickedSubtitleRef.current = null;
    }

    // 处理重复模式：使用锁定的字幕索引
    if (isRepeatMode && repeatSubtitleIndex >= 0) {
      const original = originalSubtitles[repeatSubtitleIndex];
      const translated = translatedSubtitles[repeatSubtitleIndex];
      if (original && translated) {
        const end = original.end !== undefined ? original.end : 
                   (translated.end !== undefined ? translated.end : 0);
        // 如果超过当前字幕的结束时间，跳转回开始时间
        if (time >= end) {
          const start = original.start !== undefined ? original.start : 
                       (translated.start !== undefined ? translated.start : 0);
          // 【P1】使用 player actions 进行 seek，不允许直接操作 videoRef
          playerActions.seek(start, { autoPlayAfterSeek: false });
        }
      }
    }

    // 段落索引更新：无论是否启用词级对齐，永远以原文句级 VTT 为边界、为准。
    // 词级对齐 JSON 仅用于段内的词级高亮，不再决定"当前是第几句"。
    let finalIndex: number;
    if (isRepeatMode && repeatSubtitleIndex >= 0) {
      finalIndex = repeatSubtitleIndex;
    } else {
      finalIndex = findSubtitleIndexByTime(originalSubtitles, translatedSubtitles, time);
    }

    // 如果存在"点击锁定索引"，则不允许字幕索引回退到该索引之前
    if (lockedSubtitleIndexRef.current !== null) {
      const lockedIdx = lockedSubtitleIndexRef.current;
      if (finalIndex < lockedIdx) {
        finalIndex = lockedIdx;
      }
      // 当时间明显超过锁定字幕的结束时间一段距离后，自动解除锁定
      const lockedOriginal = originalSubtitles[lockedIdx];
      const lockedTranslated = translatedSubtitles[lockedIdx];
      const lockedEnd = (lockedOriginal && lockedOriginal.end !== undefined)
        ? lockedOriginal.end
        : (lockedTranslated && lockedTranslated.end !== undefined)
          ? lockedTranslated.end
          : 0;
      if (time >= lockedEnd + 0.5) {
        lockedSubtitleIndexRef.current = null;
      }
    }

    const subtitleIndexChanged = finalIndex !== lastSubtitleIndexRef.current;

    if (subtitleIndexChanged) {
      lastSubtitleIndexRef.current = finalIndex;
      setCurrentSubtitleIndex(finalIndex);
      // 字幕索引变化时，清空词索引（开启词级时由 rAF 循环重新推进；关闭时保持为 null）
      lastWordIndexRef.current = null;
      setCurrentWordIndex(null);
    } else if (!enableWordAlignment && currentWordIndex !== null) {
      // 关闭词级对齐时不应保留任何词级高亮
      setCurrentWordIndex(null);
      lastWordIndexRef.current = null;
    }

    // 记录浏览历史（仅在视频时长有效时）
    if (videoDuration > 0 && selectedVideoId) {
      const progress = (time / videoDuration) * 100;
      recordHistory(selectedVideoId, progress);
    }
  }, [originalSubtitles, translatedSubtitles, enableWordAlignment, currentWordIndex, isRepeatMode, repeatSubtitleIndex, findSubtitleIndexByTime, videoDuration, selectedVideoId, recordHistory, updateProgressBarVisual, playerActions]);

  // 当启用词级对齐时，立即根据当前播放时间和字幕索引初始化词索引
  useEffect(() => {
    if (enableWordAlignment && displayWordData && displayWordData.segments.length > 0 && currentSubtitleIndex >= 0) {
      const video = videoRef.current;
      if (!video) return;

      const time = video.currentTime;
      const segmentIndex = currentSubtitleIndex;

      // 确保字幕索引在有效范围内
      if (segmentIndex >= 0 && segmentIndex < displayWordData.segments.length) {
        const segment = displayWordData.segments[segmentIndex];
        const words = getEffectiveWordsForSegment(segmentIndex);

        if (words && words.length > 0) {
          // 根据当前播放时间找到对应的词索引
          // 找到最后一个开始时间小于等于当前时间的词
          let wordIdx = 0;
          for (let i = 0; i < words.length; i++) {
            if (time >= words[i].start) {
              wordIdx = i;
            } else {
              break;
            }
          }

          const newWordIndex = { segmentIndex, wordIndex: wordIdx };
          // 如果当前没有词索引，或者段落索引不匹配，或者词索引需要更新，则立即更新
          // 这样可以确保在重新启用词级对齐时，立即显示正确的词高亮
          if (
            !currentWordIndex ||
            currentWordIndex.segmentIndex !== newWordIndex.segmentIndex ||
            currentWordIndex.wordIndex !== newWordIndex.wordIndex
          ) {
            setCurrentWordIndex(newWordIndex);
            lastWordIndexRef.current = newWordIndex;
          }
        } else if (currentWordIndex && currentWordIndex.segmentIndex === segmentIndex) {
          // 如果该段落没有词，但当前词索引指向这个段落，则清空
          setCurrentWordIndex(null);
          lastWordIndexRef.current = null;
        }
      }
    } else if (!enableWordAlignment && currentWordIndex !== null) {
      // 当禁用词级对齐时，清空词索引
      setCurrentWordIndex(null);
      lastWordIndexRef.current = null;
    }
  }, [enableWordAlignment, displayWordData, currentSubtitleIndex, getEffectiveWordsForSegment, currentWordIndex]);

  // 滚动到当前字幕
  useEffect(() => {
    // 如果用户当前正在用鼠标浏览/悬停字幕列表，则不抢滚动控制权，避免闪烁
    if (isSubtitleAreaHoveredRef.current) {
      return;
    }

    if (currentSubtitleIndex >= 0 && subtitleContainerRef.current) {
      const element = document.getElementById(`subtitle-${currentSubtitleIndex}`);
      if (element && subtitleContainerRef.current) {
        const container = subtitleContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        
        // 计算元素在容器中的位置
        const elementTop = elementRect.top - containerRect.top + container.scrollTop;
        const elementBottom = elementTop + elementRect.height;
        const containerHeight = container.clientHeight;
        const scrollTop = container.scrollTop;
        
        // 底部安全边距：考虑控制栏遮挡
        // 移动端控制栏高度为70px，PC端为90px
        const isMobile = window.innerWidth <= 768;
        const bottomSafeMargin = isMobile ? 70 : 90;
        
        // 计算可视区域边界（考虑底部控制栏遮挡）
        const viewTop = scrollTop;
        const viewBottom = scrollTop + containerHeight - bottomSafeMargin;
        
        // 计算理想的滚动位置（让字幕在容器上方1/3的位置）
        const idealTop = scrollTop + containerHeight / 3;
        const targetScrollTop = elementTop - containerHeight / 3;
        const distanceFromIdeal = Math.abs(elementTop - idealTop);
        
        // 判断字幕是否至少部分可见（只要有交集就算看得见）
        const isBarelyVisible = elementBottom > viewTop && elementTop < viewBottom;
        
        // 判断是否接近理想位置（在舒适区域内）
        const isCloseToIdeal = distanceFromIdeal <= 40;
        
        // 只要看不见或者明显不在舒适区域，就滚动
        if (!isBarelyVisible || !isCloseToIdeal) {
          container.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth',
          });
        }
      }
    }
  }, [currentSubtitleIndex]);

  // 【P1】safeSeek 已迁移到 usePlayerEngine，使用 playerActions.seek
  // 保留此函数作为兼容层，内部调用 playerActions.seek
  const safeSeek = useCallback((targetTime: number, onSeeked?: () => void) => {
    playerActions.seek(targetTime, { autoPlayAfterSeek: false, onSeeked });
  }, [playerActions]);

  // 【P0】handleRepeatCurrentCue 已迁移到 useSubtitleControlState，使用 subtitleControl.repeatCurrentCue
  // 启停 requestAnimationFrame 驱动的词级高亮循环
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      // 只有在启用词级对齐时才启动循环
      if (enableWordAlignment && displayWordData && displayWordData.segments.length > 0) {
        rafIdRef.current = requestAnimationFrame(tickWordHighlight);
      }
    };

    const onPauseOrEnded = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPauseOrEnded);
    video.addEventListener('ended', onPauseOrEnded);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPauseOrEnded);
      video.removeEventListener('ended', onPauseOrEnded);
    };
  }, [tickWordHighlight, enableWordAlignment, displayWordData]);

  // 当启用词级对齐且视频正在播放时，立即启动 requestAnimationFrame 循环
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // 如果启用词级对齐、有数据、视频正在播放且没有暂停，则启动循环
    if (
      enableWordAlignment &&
      displayWordData &&
      displayWordData.segments.length > 0 &&
      !video.paused &&
      !video.ended
    ) {
      // 如果循环没有运行，则启动它
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(tickWordHighlight);
      }
    } else {
      // 如果禁用词级对齐或没有数据，停止循环
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    }
  }, [enableWordAlignment, displayWordData, tickWordHighlight]);

  // 点击字幕跳转到对应时间（使用 useCallback 优化性能，避免每次渲染都创建新函数）
  const handleSubtitleClick = useCallback((startTime: number, index: number, e?: React.MouseEvent | React.TouchEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // 记录点击的字幕信息（用于后续时间更新时的匹配）
    lastClickedSubtitleRef.current = {
      index: index,
      time: startTime,
      timestamp: Date.now()
    };
    // 同时记录"锁定索引"：在锁定期间内，不允许字幕索引回退到该索引之前
    lockedSubtitleIndexRef.current = index;
    
    // 立即更新字幕索引和词索引，确保界面立即响应
    setCurrentSubtitleIndex(index);
    lastSubtitleIndexRef.current = index;
    
    // 如果开启了重复模式，更新锁定的字幕索引到新点击的字幕
    if (isRepeatMode && index >= 0) {
      setRepeatSubtitleIndex(index);
    }
    
    // 如果有词级对齐数据且开启了词级对齐，立即设置词索引为该段落中的第一个"有效词"
    // "有效词"通过 getEffectiveWordsForSegment 获取，数据源由 wordHighlightMode 决定（display / raw）。
    if (enableWordAlignment && displayWordData && displayWordData.segments.length > 0 && index < displayWordData.segments.length) {
      const words = getEffectiveWordsForSegment(index);
      if (words && words.length > 0) {
        // 默认选中该段落中的第一个 displayWord
        const newWordIndex = { segmentIndex: index, wordIndex: 0 };
        setCurrentWordIndex(newWordIndex);
        lastWordIndexRef.current = newWordIndex;
      } else {
        setCurrentWordIndex(null);
        lastWordIndexRef.current = null;
      }
    } else {
      setCurrentWordIndex(null);
      lastWordIndexRef.current = null;
    }
    
    // 使用 requestAnimationFrame 而不是 setTimeout，确保在下一帧执行，减少延迟
    requestAnimationFrame(() => {
      if (!videoRef.current) return;
      
      const video = videoRef.current;
      
      if (typeof startTime !== 'number' || isNaN(startTime) || startTime < 0) {
        return;
      }
      
      // HLS 视频需要等待 canplay 事件才能可靠地 seek
      // readyState < 2 (HAVE_METADATA) 是 MP4 时代的逻辑，在 HLS 上不可靠
      if (video.readyState < 3) { // HAVE_FUTURE_DATA (3) 表示有足够数据可以播放
        const onCanPlay = () => {
          video.removeEventListener('canplay', onCanPlay);
          if (videoRef.current && videoRef.current === video) {
            // 检查时长是否有效
            if (video.duration && startTime > video.duration) {
              return;
            }
            
            // 【P1】使用 player actions 进行 seek，点击字幕后自动播放
            playerActions.seek(startTime, {
              autoPlayAfterSeek: true, // 点击字幕后自动播放
            });
          }
        };
        video.addEventListener('canplay', onCanPlay, { once: true });
        return;
      }
      
      if (video.duration && startTime > video.duration) {
        return;
      }
      
      try {
        // 【P1】使用 player actions 进行 seek，点击字幕后自动播放
        playerActions.seek(startTime, {
          autoPlayAfterSeek: true, // 点击字幕后自动播放
        });
      } catch (error) {
        // 设置视频时间失败，忽略错误
      }
    });
  }, [enableWordAlignment, displayWordData, isRepeatMode, getEffectiveWordsForSegment, playerActions]);

  // 格式化时间显示（用于进度条，MM:SS格式）
  const formatTime = useCallback((seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // 格式化时间显示（用于字幕，包含毫秒）
  const formatTimeWithMs = useCallback((seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '00:00.000';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  }, []);

  // ============================================
  // 性能优化：将字幕项提取为 React.memo 组件
  // ============================================
  
  // SubtitleItem 组件的 Props 接口
  interface SubtitleItemProps {
    index: number;
    original: Subtitle | undefined;
    translated: Subtitle | undefined;
    isActive: boolean;
    isPast: boolean;
    currentWordIndex: { segmentIndex: number; wordIndex: number } | null;
    enableWordAlignment: boolean;
    descriptor: VideoDescriptor | null;
    displaySettings: Record<LanguageCode, LanguageDisplaySettings>;
    subtitleViewMode: SubtitleViewMode;
    kuromojiReady: boolean;
    kuromojiTokenizerRef: React.MutableRefObject<Tokenizer | null>;
    onSubtitleClick: (startTime: number, index: number, e?: React.MouseEvent | React.TouchEvent) => void;
    formatTimeWithMs: (seconds: number) => string;
    convertToRuby: (
      text: string,
      showFurigana: boolean,
      showRomaji: boolean,
      baseStyle?: React.CSSProperties
    ) => React.ReactNode;
    convertWordToThreeLayer: (surface: string, reading: string, showFurigana: boolean, showRomaji: boolean, baseStyle?: React.CSSProperties) => React.ReactNode;
  }

  // SubtitleItem 组件实现
  const SubtitleItem: React.FC<SubtitleItemProps> = ({
    index,
    original,
    translated,
    isActive,
    isPast,
    currentWordIndex,
    enableWordAlignment,
    descriptor,
    displaySettings,
    subtitleViewMode,
    kuromojiReady,
    kuromojiTokenizerRef,
    onSubtitleClick,
    formatTimeWithMs,
    convertToRuby,
    convertWordToThreeLayer,
  }) => {
    if (!original && !translated) return null;

    const start = (original && original.start !== undefined) ? original.start : 
                 (translated && translated.start !== undefined) ? translated.start : 0;
    const end = (original && original.end !== undefined) ? original.end : 
               (translated && translated.end !== undefined) ? translated.end : 0;

    const originalText = (original && original.text) ? original.text.trim() : '';
    const translatedText = (translated && translated.text) ? translated.text.trim() : '';

    // 检测是否为日语
    const language = descriptor?.sourceLanguage || '';
    const isJapanese = language.toLowerCase() === 'ja' || 
                     /[\u3040-\u309F\u30A0-\u30FF\u4e00-\u9faf]/.test(originalText);
    
    // 获取当前语言的显示设置
    const sourceLanguage: string = descriptor?.sourceLanguage ?? 'ja';
    
    // 判断语言类型：优先使用 sourceLanguage，最后通过文本内容判断
    const isEnglish = language.toLowerCase() === 'en' || 
                     sourceLanguage.toLowerCase() === 'en' ||
                     (!isJapanese && /^[a-zA-Z\s.,!?'";:()-]+$/.test(originalText) && originalText.length > 0);
    
    // 确定使用的语言代码（用于查找设置）
    const effectiveLanguageCode = isEnglish ? 'en' : (isJapanese ? 'ja' : sourceLanguage);
    const currentLangSettings: LanguageDisplaySettings = displaySettings[effectiveLanguageCode] ?? {
      showFurigana: effectiveLanguageCode === 'ja', // 日语默认显示假名
      showRomaji: false,                     // 默认不显示罗马字
      showIpa: false,                        // 英语默认不显示 IPA
    };
    
    const showFurigana = isJapanese ? (currentLangSettings.showFurigana ?? true) : false;
    const showRomaji = isJapanese ? (currentLangSettings.showRomaji ?? false) : false;
    const showIpa = isEnglish ? (currentLangSettings.showIpa ?? false) : false;

    // 渲染英语原文 + IPA
    const renderEnglishWithIpa = (text: string): React.ReactNode => {
      const ipaWords: IpaWord[] = textToIpaWords(text);

      if (!showIpa) {
        // 不显示 IPA 时，仍然使用 en-word-block 结构保持一致性
        return (
          <div className="subtitle-original-english">
            {ipaWords.map((w, idx) => (
              <span key={idx} className="en-word-block">
                <span className="en-word-text">{w.surface}</span>
              </span>
            ))}
          </div>
        );
      }

      return (
        <div className="subtitle-original-english">
          {ipaWords.map((w, idx) => (
            <span key={idx} className="en-word-block">
              <span className="en-word-text">{w.surface}</span>
              <span className="en-word-ipa">
                {w.ipa ?? ''}
              </span>
            </span>
          ))}
        </div>
      );
    };

    // 如果有词级对齐数据，渲染带词级高亮的文本
    const renderTextWithWords = (text: string, segmentIndex: number) => {
      if (!displayWordData) {
        return text; // 没有词级数据，返回普通文本
      }

      const langCode = (displayWordData?.language || descriptor?.sourceLanguage || '').toLowerCase();
      const isJapanese = langCode === 'ja';
      const isEnglishInWords = langCode === 'en';
      const needsSpacing = !['ja', 'zh', 'zh-cn', 'zh-tw', 'ko', 'th'].includes(langCode);

      const words = getEffectiveWordsForSegment(segmentIndex);
      if (!words || words.length === 0) {
        // 没有词级数据：日语直接整句 ruby，其他语言返回原文
        if (isJapanese && kuromojiReady) {
          return convertToRuby(text, showFurigana, showRomaji);
        }
        return text;
      }
      const isCurrentSegment = currentWordIndex?.segmentIndex === segmentIndex;
      const isActiveSubtitle = isActive; // 当前字幕是否被选中
      
      // 【日语 + 词级对齐】：
      // - displayWord（来自当前生效的词级 JSON）作为"高亮单位"
      // - displayWord.text 内部再用 convertToRuby 做 kuromoji + 假名渲染
      // - 高亮样式仅通过 .jp-display-word.word-active 这个 class + CSS 控制：
      //   - 外层容器画整体 outline
      //   - 内部 ruby-base（原文）统一背景，不对 ruby-top/rt（假名）着色
      // 注意：即使 enableWordAlignment 为 false，也保持相同的 DOM 结构，避免重排
      if (isJapanese && kuromojiReady) {
        return (
          <>
            {words.map((word, wordIdx) => {
              const isCurrentWord =
                isCurrentSegment &&
                currentWordIndex?.wordIndex === wordIdx;
              const isLastWord = wordIdx === words.length - 1;

              const colorIndex = wordIdx % BACKGROUND_COLORS.length;
              const backgroundColor = BACKGROUND_COLORS[colorIndex];

              // 选中字幕时：该行所有 displayWord 都根据词级对齐着色，
              // 相邻词使用不同的背景色（循环 BACKGROUND_COLORS）。
              // 如果 enableWordAlignment 为 false，不应用背景色，但保持相同的 DOM 结构
              const baseStyle: React.CSSProperties = isActiveSubtitle && enableWordAlignment
                ? {
                    backgroundColor,
                    borderRadius: 0,
                  }
                : {};

              return (
                <React.Fragment key={wordIdx}>
                  <span
                    className={`jp-display-word ${
                      enableWordAlignment && isCurrentWord ? 'word-active' : ''
                    }`}
                    data-word-index={wordIdx}
                  >
                    {convertToRuby(word.word, showFurigana, showRomaji, baseStyle)}
                  </span>
                  {/* 日语内部不额外插入空格，由日文本身的标点/空白决定换行 */}
                  {!isJapanese && needsSpacing && !isLastWord && ' '}
                </React.Fragment>
              );
            })}
          </>
        );
      }

      // 如果是英语且需要显示 IPA，使用每个单词一个竖向容器的布局
      if (isEnglishInWords && showIpa) {
        return (
          <div className="subtitle-original-english">
            {words.map((word, wordIdx) => {
              const isCurrentWord =
                isCurrentSegment &&
                currentWordIndex?.wordIndex === wordIdx;
              const isLastWord = wordIdx === words.length - 1;
              const colorIndex = wordIdx % BACKGROUND_COLORS.length;
              const backgroundColor = BACKGROUND_COLORS[colorIndex];
              const ipa = lookupIpa(word.word);
              
              // 背景色作用于 en-word-text（词级高亮底色），选中框由 CSS 伪元素统一绘制
              // 始终应用相同的 padding 和 margin，只改变背景色，避免盒模型变化导致重排
              // 如果 enableWordAlignment 为 false，不应用背景色，但保持相同的盒模型
              const textStyle: React.CSSProperties = {
                padding: '0px 3px',  // 始终应用 padding，保持盒模型稳定
                borderRadius: '4px',
                transition: 'background-color 0.2s ease',
                ...(isActiveSubtitle && enableWordAlignment ? {
                  backgroundColor: backgroundColor,
                } : {
                  backgroundColor: 'transparent',  // 未选中时透明，但保持相同的盒模型
                }),
              };
              
              return (
                <React.Fragment key={wordIdx}>
                  <span
                    className={`en-word-block word ${enableWordAlignment && isCurrentWord ? 'word-active' : ''}`}
                    style={{
                      margin: '0 2px',  // 始终应用 margin，保持间距稳定
                    }}
                  >
                    <span className="en-word-text" style={textStyle}>
                      {word.word}
                    </span>
                    <span className="en-word-ipa">
                      {ipa ?? ''}
                    </span>
                  </span>
                  {needsSpacing && !isLastWord && ' '}
                </React.Fragment>
              );
            })}
          </div>
        );
      }

      // 其他情况：日语或英语不显示 IPA
      // 如果是英语，需要使用 en-word-block 类以应用绿色跟读框样式
      if (isEnglishInWords) {
        return (
          <div className="subtitle-original-english">
            {words.map((word, wordIdx) => {
              const isCurrentWord =
                isCurrentSegment &&
                currentWordIndex?.wordIndex === wordIdx;
              const isLastWord = wordIdx === words.length - 1;
              const colorIndex = wordIdx % BACKGROUND_COLORS.length;
              const backgroundColor = BACKGROUND_COLORS[colorIndex];
              
              // 背景色作用于 en-word-text（词级高亮底色），选中框由 CSS 伪元素统一绘制
              // 始终应用相同的 padding 和 margin，只改变背景色，避免盒模型变化导致重排
              // 如果 enableWordAlignment 为 false，不应用背景色，但保持相同的盒模型
              const textStyle: React.CSSProperties = {
                padding: '0px 3px',  // 始终应用 padding，保持盒模型稳定
                borderRadius: '4px',
                transition: 'background-color 0.2s ease',
                ...(isActiveSubtitle && enableWordAlignment ? {
                  backgroundColor: backgroundColor,
                } : {
                  backgroundColor: 'transparent',  // 未选中时透明，但保持相同的盒模型
                }),
              };
              
              return (
                <React.Fragment key={wordIdx}>
                  <span
                    className={`en-word-block word ${enableWordAlignment && isCurrentWord ? 'word-active' : ''}`}
                    style={{
                      margin: '0 2px',  // 始终应用 margin，保持间距稳定
                    }}
                  >
                    <span className="en-word-text" style={textStyle}>
                      {word.word}
                    </span>
                  </span>
                  {needsSpacing && !isLastWord && ' '}
                </React.Fragment>
              );
            })}
          </div>
        );
      }
      
      // 日语或其他语言的情况
      return (
        <>
          {words.map((word, wordIdx) => {
            const isCurrentWord =
              isCurrentSegment &&
              currentWordIndex?.wordIndex === wordIdx;
            const isLastWord = wordIdx === words.length - 1;
            
            // 为每个词分配一个颜色（循环使用），应用到背景上
            const colorIndex = wordIdx % BACKGROUND_COLORS.length;
            const backgroundColor = BACKGROUND_COLORS[colorIndex];
            
            // 背景色只作用于 ruby-base，不包含假名和 romaji；当前词的"选中框"交给 CSS 伪元素处理，
            // 避免在 active 状态下修改 padding / border 等影响盒模型的属性，防止突兀重排。
            // 如果 enableWordAlignment 为 false，不应用背景色，但保持相同的盒模型
            const baseStyle: React.CSSProperties = isActiveSubtitle && enableWordAlignment
              ? {
                  backgroundColor: backgroundColor,
                  padding: '0px 3px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease',
                }
              : {};
            
            // 对于日语，将词拆成若干 token，对"包含汉字的 token" 单独注音，
            // 使假名对齐到具体汉字头上；其它 token 只显示原文，但共享同一底色/高亮。
            let wordContent: React.ReactNode;
            if (isJapanese && kuromojiReady) {
              try {
                const tokenizer = kuromojiTokenizerRef.current;
                if (!tokenizer) {
                  return word.word; // tokenizer 未就绪，返回原始文本
                }
                const tokens = tokenizer.tokenize(word.word);
                const children: React.ReactNode[] = [];

                tokens.forEach((token, idxToken) => {
                  const surfaceToken = token.surface_form || '';
                  if (!surfaceToken) {
                    return;
                  }
                  const readingToken = token.reading || token.pronunciation || surfaceToken;

                  children.push(
                    <React.Fragment key={idxToken}>
                      {convertWordToThreeLayer(
                        surfaceToken,   // surface：单个 token 文本（可能包含汉字，也可能只是假名/符号）
                        readingToken,   // reading：该 token 的读音；内部只会对含汉字的 token 显示假名
                        showFurigana,
                        showRomaji,
                        baseStyle
                      )}
                    </React.Fragment>
                  );
                });

                wordContent = <>{children}</>;
              } catch (error) {
                // tokenize 出错时，仍然作为整体一个块处理
                wordContent = convertWordToThreeLayer(word.word, '', showFurigana, showRomaji, baseStyle);
              }
            } else {
              // 非日语或 kuromoji 未就绪，直接显示原文，样式应用到word容器
              wordContent = word.word;
            }
            
            return (
              <React.Fragment key={wordIdx}>
                <span
                  className={`word ${enableWordAlignment && isCurrentWord ? 'word-active' : ''}`}
                  style={{
                    margin: isActiveSubtitle ? '0 2px' : '0',
                  // 非日语词时，仅在整行激活时给容器加柔和底色，不再在这里画 outline，
                  // 避免 currentWord 切换时因为边框/offset 改变导致文本位置跳动。
                  // 如果 enableWordAlignment 为 false，不应用背景色，但保持相同的盒模型
                    ...(isJapanese && kuromojiReady ? {} : (isActiveSubtitle && enableWordAlignment ? {
                      backgroundColor: backgroundColor,
                      padding: '0px 3px',
                      borderRadius: '4px',
                    transition: 'background-color 0.2s ease',
                    } : {})),
                  }}
                >
                  {wordContent}
                </span>
                {needsSpacing && !isLastWord && ' '}
              </React.Fragment>
            );
          })}
        </>
      );
    };

    // 根据字幕显示模式决定显示哪些块
    const showOriginal = subtitleViewMode === 'both' || subtitleViewMode === 'original';
    const showTranslated = subtitleViewMode === 'both' || subtitleViewMode === 'translated';

    return (
      <div
        id={`subtitle-${index}`}
        className={`subtitle-item ${isActive ? 'active' : ''} ${isPast ? 'past' : ''}`}
        onClick={(e) => onSubtitleClick(start, index, e)}
        onTouchEnd={(e) => {
          // 移动端触摸事件：立即处理，减少延迟
          e.preventDefault();
          e.stopPropagation();
          onSubtitleClick(start, index, e);
        }}
        style={{ cursor: 'pointer' }}
        title={`点击跳转到 ${formatTimeWithMs(start)}`}
      >
        {showOriginal && originalText && (
          <div className="subtitle-original">
            {displayWordData
              ? renderTextWithWords(originalText, index)
              : (() => {
                  // 没有词级对齐视图数据时，根据语言类型渲染
                  if (isJapanese && kuromojiReady) {
                    return convertToRuby(originalText, showFurigana, showRomaji);
                  } else if (isEnglish) {
                    return renderEnglishWithIpa(originalText);
                  }
                  return (
                    <div className="subtitle-original-line">
                      {originalText}
                    </div>
                  );
                })()}
          </div>
        )}
        {showTranslated && translatedText && (
          <div className="subtitle-translated">
            {translatedText}
          </div>
        )}
      </div>
    );
  };

  // 自定义比较函数：只在关键 props 变化时才重新渲染
  const areSubtitleItemsEqual = (prevProps: SubtitleItemProps, nextProps: SubtitleItemProps): boolean => {
    return (
      prevProps.index === nextProps.index &&
      prevProps.isActive === nextProps.isActive &&
      prevProps.isPast === nextProps.isPast &&
      prevProps.subtitleViewMode === nextProps.subtitleViewMode &&
      prevProps.currentWordIndex?.segmentIndex === nextProps.currentWordIndex?.segmentIndex &&
      prevProps.currentWordIndex?.wordIndex === nextProps.currentWordIndex?.wordIndex &&
      prevProps.original === nextProps.original &&
      prevProps.translated === nextProps.translated &&
      // displaySettings 的引用比较（如果引用没变，说明设置没变）
      prevProps.displaySettings === nextProps.displaySettings &&
      prevProps.enableWordAlignment === nextProps.enableWordAlignment &&
      prevProps.descriptor === nextProps.descriptor &&
      prevProps.kuromojiReady === nextProps.kuromojiReady
    );
  };

  // 使用 React.memo 包装组件
  const MemoizedSubtitleItem = React.memo(SubtitleItem, areSubtitleItemsEqual);


  // 处理返回按钮点击：先保存进度，再跳转
  const handleBackClick = useCallback(async () => {
    // 清除防抖定时器，立即保存当前进度
    if (historyRecordTimeoutRef.current) {
      clearTimeout(historyRecordTimeoutRef.current);
      historyRecordTimeoutRef.current = null;
    }

    // 如果有视频且时长有效，立即保存当前进度
    if (selectedVideoId && videoRef.current && videoRef.current.duration > 0) {
      const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      // 立即保存，不等待完成（避免阻塞用户操作）
      performRecordHistory(selectedVideoId, currentProgress).catch(() => {
        // 静默失败，不影响用户体验
      });
    }

    // 根据来源页面返回
    switch (fromPage) {
      case 'history':
        navigate('/profile?tab=history');
        break;
      case 'management':
        navigate('/profile');
        break;
      case 'upload':
        navigate('/upload');
        break;
      case 'collection':
        // 带 collectionId 时回到具体合集页，并把 returnTo 透传回去（state），
        // 让 CollectionDetail 的返回按钮能继续回到最初来源；否则回到首页"合集"tab
        if (fromCollectionId) {
          navigate(
            `/collections/${encodeURIComponent(fromCollectionId)}`,
            fromReturnTo ? { state: { returnTo: fromReturnTo } } : undefined,
          );
        } else {
          navigate('/?tab=collections');
        }
        break;
      case 'list':
      default:
        navigate('/');
        break;
    }
  }, [selectedVideoId, fromPage, fromCollectionId, fromReturnTo, navigate, performRecordHistory]);

  // AppShell header: video-header
  const header = (
    <div className="video-header app-shell-header-content">
      <button
        className="header-back-button"
        onClick={(e) => {
          e.stopPropagation();
          handleBackClick();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleBackClick();
        }}
        title={
          fromPage === 'history'
            ? '返回浏览历史'
            : fromPage === 'management'
            ? '返回我的视频'
            : fromPage === 'upload'
            ? '返回上传页面'
            : fromPage === 'collection'
            ? '返回合集'
            : '返回视频列表'
        }
        aria-label={
          fromPage === 'history'
            ? '返回浏览历史'
            : fromPage === 'management'
            ? '返回我的视频'
            : fromPage === 'upload'
            ? '返回上传页面'
            : fromPage === 'collection'
            ? '返回合集'
            : '返回视频列表'
        }
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
      </button>
      <div className="header-content">
        <div className="header-title">
          {displayTitle}
        </div>
        {descriptor?.username && (
          <div className="header-uploader">
            上传者：{descriptor.username}
          </div>
        )}
      </div>
    </div>
  );

  // AppShell footer: control-area and display-settings-panel
  const footer = (
    <>
      {/* 控制区 - 进度条和播放控制 */}
      {/* 【建议1】移除 style 中的 --progress，改为通过 ref + requestAnimationFrame 直接更新 */}
      <div
        ref={controlAreaRef}
        className="control-area"
      >
        {/* 【建议5】使用拆分的组件 */}
        <ProgressBar
          currentTime={currentTime}
          displayTime={displayTime}
          videoDuration={videoDuration}
          disabled={descriptor?.hlsStatus !== 'ready' || !hlsUrl || !!hlsError || videoDuration <= 0}
          formatTime={formatTime}
          controlAreaRef={controlAreaRef as React.RefObject<HTMLDivElement | null>}
          isDraggingRef={isDraggingRef}
          wasPlayingBeforeDragRef={wasPlayingBeforeDragRef}
          draggingValueRef={draggingValueRef}
          videoRef={videoRef as React.RefObject<HTMLVideoElement | null>}
          safeSeek={safeSeek}
          playerActions={playerActions}
          setCurrentTime={setCurrentTime}
          setDisplayTime={setDisplayTime}
        />
        <div className="control-buttons">
          <ControlsLeft
            isRepeatMode={isRepeatMode}
            canRepeatCurrentCue={subtitleControl.canRepeatCurrentCue}
            playbackRate={playbackRate}
            isSpeedSelectorOpen={isSpeedSelectorOpen}
            disabled={descriptor?.hlsStatus !== 'ready' || !hlsUrl || !!hlsError || videoDuration <= 0}
            onRepeatClick={subtitleControl.repeatCurrentCue}
            onPlaybackRateChange={(newRate) => {
              setPlaybackRate(newRate);
              // 【P1】使用 player actions，不再直接操作 videoRef
              playerActions.setPlaybackRate(newRate);
            }}
            onSpeedSelectorOpenChange={setIsSpeedSelectorOpen}
          />
          <ControlsCenter
            isPlaying={isPlaying}
            disabled={descriptor?.hlsStatus !== 'ready' || !hlsUrl || !!hlsError || videoDuration <= 0}
            onPlayPauseClick={() => {
              // 【P1】使用 player actions，不再直接操作 videoRef
              playerActions.togglePlay();
            }}
          />
          <ControlsRight
            subtitleModeLabel={subtitleControl.subtitleModeLabel}
            showDisplaySettingsPanel={showDisplaySettingsPanel}
            disabled={descriptor?.hlsStatus !== 'ready' || !hlsUrl || !!hlsError || videoDuration <= 0}
            onSubtitleModeClick={subtitleControl.toggleSubtitleMode}
            onDisplaySettingsClick={() => setShowDisplaySettingsPanel(prev => !prev)}
          />
        </div>
      </div>

    </>
  );

  // AppShell body: video-area and subtitle-area
  const body = (
    <div className="player-body-container">
      {/* 视频区 */}
      {descriptor?.hlsStatus === 'ready' && hlsUrl && !hlsError && (
        <div className="video-area">
          <div className="video-wrapper">
            <video
              key={selectedVideoId}
              ref={mergedVideoRef}
              // ⚠️ 重要：hls.js 模式下不设置 src（iOS Safari 原生模式会由 Hook 设置）
              // 移动端内联播放：防止 iOS Safari 自动全屏
              // playsInline 是现代标准属性（React 会自动转换为 playsinline）
              playsInline
              // webkit-playsinline 是 iOS Safari 的旧版兼容属性（React 不支持，但浏览器需要）
              // 使用 setAttribute 在 onLoadedMetadata 中设置，这里不设置
              // x5-playsinline 是 Android 微信浏览器的兼容属性
              // 使用 setAttribute 在 onLoadedMetadata 中设置，这里不设置
              // 禁用原生控件：避免用户点击原生进度条触发系统播放器
              // 原生控件在移动端（特别是 iOS）会强制全屏，破坏内联播放体验
              controls={false}
              // 禁用画中画：防止意外触发画中画模式
              disablePictureInPicture
              onTimeUpdate={handleTimeUpdate}
              onSeeked={() => {
                setTimeout(() => {
                  handleTimeUpdate();
                }, 50);
              }}
              onLoadedMetadata={() => {
                if (videoRef.current) {
                  const duration = videoRef.current.duration || 0;
                  setVideoDuration(duration);
                  // 【P1】使用 player actions 设置倍速，不再直接操作 videoRef
                  playerActions.setPlaybackRate(playbackRate);
                  // 确保内联播放属性在元数据加载后仍然有效
                  // 这些属性必须在每次元数据加载后重新设置，防止某些浏览器重置
                  videoRef.current.setAttribute('playsinline', 'true');
                  videoRef.current.setAttribute('webkit-playsinline', 'true');
                  videoRef.current.setAttribute('x5-playsinline', 'true');
                  // 【建议1】初始化进度条 CSS 变量
                  if (controlAreaRef.current && duration > 0) {
                    updateProgressBarVisual(0, duration);
                  }
                }
              }}
              onCanPlay={() => {
                // 在移动端，HLS 视频需要等待 canplay 事件才能可靠地 seek
                if (videoRef.current && !hasAppliedInitialProgressRef.current) {
                  const duration = videoRef.current.duration || 0;
                  
                  // 如果是从浏览历史进入，并且有有效的进度百分比，则在视频可播放后恢复到对应进度
                  if (
                    fromPage === 'history' &&
                    typeof initialProgressFromState === 'number' &&
                    initialProgressFromState > 0 &&
                    initialProgressFromState < 100 &&
                    duration > 0
                  ) {
                    const clampedProgress = Math.min(Math.max(initialProgressFromState, 0), 100);
                    const targetTime = (duration * clampedProgress) / 100;
                    
                    // 【P1】使用 player actions 进行 seek，不再直接操作 videoRef
                    const handleSeeked = () => {
                      if (videoRef.current) {
                        const seekedTime = playerActions.getCurrentTime();
                        setCurrentTime(seekedTime);
                        // 【建议6】seek 完成后立即更新显示时间（不节流）
                        setDisplayTime(seekedTime);
                        lastDisplayTimeUpdateRef.current = Date.now();
                        lastDisplayTimeRef.current = seekedTime;
                        // 【建议1】更新进度条视觉
                        if (controlAreaRef.current && duration > 0) {
                          updateProgressBarVisual(seekedTime, duration);
                        }
                        // 手动触发一次时间更新，以更新字幕索引和滚动位置
                        handleTimeUpdate();
                        hasAppliedInitialProgressRef.current = true;
                      }
                    };
                    
                    playerActions.seek(targetTime, {
                      autoPlayAfterSeek: false,
                      onSeeked: handleSeeked,
                    });
                  } else {
                    // 不是从浏览历史进入，或者没有进度，设置为 0
                    setCurrentTime(0);
                    setDisplayTime(0);
                    lastDisplayTimeUpdateRef.current = 0;
                    lastDisplayTimeRef.current = 0;
                    // 【建议1】初始化进度条 CSS 变量
                    if (controlAreaRef.current && duration > 0) {
                      updateProgressBarVisual(0, duration);
                    }
                    // 【P1】使用 player actions 进行 seek，不再直接操作 videoRef
                    playerActions.seek(0, { autoPlayAfterSeek: false });
                    hasAppliedInitialProgressRef.current = true;
                  }
                }
              }}
              onPlay={() => {
                setIsPlaying(true);

                // 【规则2】first play 立即写：保证"短观看"也算真实观看
                if (!hasRecordedFirstPlayRef.current && selectedVideoId && videoRef.current) {
                  const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
                  recordHistory(selectedVideoId, currentProgress, true); // immediate = true
                  hasRecordedFirstPlayRef.current = true;
                }
              }}
              onPause={() => setIsPlaying(false)}
              // 防止视频元素获得焦点时触发全屏
              onFocus={(e) => {
                e.preventDefault();
                e.currentTarget.blur();
              }}
              // 防止双击视频元素触发全屏（某些浏览器的默认行为）
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              // 防止右键菜单（可能包含全屏选项）
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              // 点击视频区域：PC / 平板 / 手机统一切换 播放 / 暂停
              // 同时阻止事件冒泡，避免触发外层潜在的全屏或其它行为
              onClick={(e) => {
                e.stopPropagation();
                // 如果已经通过触摸事件处理过，则忽略点击事件（防止移动端重复触发）
                if (touchHandledRef.current) {
                  touchHandledRef.current = false;
                  return;
                }
                // 【P1】使用 player actions，不再直接操作 videoRef
                playerActions.togglePlay().catch(() => {
                  // 某些浏览器需要用户手势，失败时忽略即可
                });
              }}
              // 移动端：触摸结束时同样触发播放 / 暂停，防止长按等触发原生行为
              onTouchStart={(e) => {
                // 阻止触摸事件冒泡，防止触发视频元素的原生行为（如系统级控件）
                e.stopPropagation();
                touchHandledRef.current = false; // 重置标志
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                e.preventDefault(); // 阻止后续的 click 事件
                // 【P1】使用 player actions，不再直接操作 videoRef
                touchHandledRef.current = true;
                playerActions.togglePlay().catch(() => {
                  // 忽略播放失败
                });
                // 延迟重置标志，确保 click 事件能检测到
                setTimeout(() => {
                  touchHandledRef.current = false;
                }, 300);
              }}
              preload="auto"
            />
            {/* 中央播放/暂停指示图标（仅做视觉反馈，不拦截点击） */}
            <div
              className={`video-center-indicator ${isPlaying ? 'playing' : 'paused'}`}
              aria-hidden="true"
            >
              <div className="video-center-icon">
                {isPlaying ? (
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="5" width="4" height="14" rx="1.5" />
                    <rect x="14" y="5" width="4" height="14" rx="1.5" />
                  </svg>
                ) : (
                  <svg width="42" height="42" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HLS 状态显示：pending */}
      {descriptor?.hlsStatus === 'pending' && (
        <div className="video-area">
          <div className="video-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
            <div style={{ textAlign: 'center', color: '#666' }}>
              <Clock size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <div>视频正在转码中...</div>
              <div style={{ fontSize: '14px', marginTop: '8px' }}>
                进度: {descriptor.progress || 0}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HLS 状态显示：error */}
      {descriptor?.hlsStatus === 'error' && (
        <div className="video-area">
          <div className="video-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
            <div style={{ textAlign: 'center', color: '#ef4444' }}>
              <XCircle size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <div>HLS 生成失败</div>
              <div style={{ fontSize: '14px', marginTop: '8px' }}>
                请重新上传视频
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HLS 播放错误显示 */}
      {hlsError && (
        <div className="video-area">
          <div className="video-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
            <div style={{ textAlign: 'center', color: '#ef4444' }}>
              <XCircle size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <div>播放失败</div>
              <div style={{ fontSize: '14px', marginTop: '8px' }}>
                {hlsError.message}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 字幕区 */}
      {originalSubtitles.length > 0 && translatedSubtitles.length > 0 && (
        <div
          className="subtitle-area"
          ref={subtitleContainerRef}
          onMouseEnter={() => {
            isSubtitleAreaHoveredRef.current = true;
          }}
          onMouseLeave={() => {
            isSubtitleAreaHoveredRef.current = false;
          }}
        >
          {/* 字幕设置开关 - 已移除，现在通过控制栏的 A/a 按钮控制 */}
          <div className="subtitle-container">
            {Array.from({
              length: Math.max(originalSubtitles.length, translatedSubtitles.length),
            }).map((_, index) => {
              const original = originalSubtitles[index];
              const translated = translatedSubtitles[index];
              const end = (original && original.end !== undefined) ? original.end :
                         (translated && translated.end !== undefined) ? translated.end : 0;

              return (
                <MemoizedSubtitleItem
                  key={index}
                  index={index}
                  original={original}
                  translated={translated}
                  isActive={currentSubtitleIndex === index}
                  isPast={currentTime >= end}
                  currentWordIndex={currentWordIndex}
                  enableWordAlignment={enableWordAlignment}
                  descriptor={descriptor}
                  displaySettings={displaySettings}
                  subtitleViewMode={subtitleViewMode}
                  kuromojiReady={kuromojiReady}
                  kuromojiTokenizerRef={kuromojiTokenizerRef}
                onSubtitleClick={handleSubtitleClick}
                  formatTimeWithMs={formatTimeWithMs}
                  convertToRuby={convertToRuby}
                  convertWordToThreeLayer={convertWordToThreeLayer}
                />
              );
                })}
            {/* 底部小垫块，让最后的字幕不要太贴合最下面 */}
            <div className="subtitle-bottom-spacer"></div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <AppShell header={header} footer={footer} bodyScrollable={false}>
        {body}
      </AppShell>

      {/* 显示设置面板 - 独立于AppShell布局 */}
      {showDisplaySettingsPanel && (
        <div className="display-settings-panel">
          {/* 语言专用设置区域 */}
          <div className="display-settings-section">
            {(() => {
              const sourceLanguage: string = descriptor?.sourceLanguage ?? 'ja';

              // 检测当前字幕内容是否为英语（用于面板显示）
              const hasEnglishContent = originalSubtitles.length > 0 &&
                originalSubtitles.some(sub => {
                  const text = sub.text || '';
                  return /^[a-zA-Z\s.,!?'";:()-]+$/.test(text) &&
                         !/[\u3040-\u309F\u30A0-\u30FF\u4e00-\u9faf]/.test(text) &&
                         text.length > 0;
                });

              // 确定显示哪个语言的设置
              const displayLang = sourceLanguage === 'ja' ? 'ja' :
                                 (sourceLanguage === 'en' || hasEnglishContent ? 'en' : sourceLanguage);

              const currentLangSettings: LanguageDisplaySettings = displaySettings[displayLang] ?? {
                showFurigana: displayLang === 'ja',
                showRomaji: false,
                showIpa: false,
              };

              if (displayLang === 'ja') {
                return (
                  <>
                    <div className="display-settings-title">显示内容</div>
                    <label 
                      className="display-settings-item"
                      onTouchEnd={(e) => {
                        // 移动端触摸事件：立即处理，减少延迟
                        e.preventDefault();
                        e.stopPropagation();
                        const checkbox = e.currentTarget.querySelector('input[type="checkbox"]') as HTMLInputElement;
                        if (checkbox && !checkbox.disabled) {
                          checkbox.click();
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={currentLangSettings.showFurigana ?? true}
                        onChange={e => {
                          const checked = e.target.checked;
                          setDisplaySettings(prev => ({
                            ...prev,
                            [sourceLanguage]: {
                              ...currentLangSettings,
                              showFurigana: checked,
                            },
                          }));
                        }}
                      />
                      <span>显示假名</span>
                    </label>
                    <label 
                      className="display-settings-item"
                      onTouchEnd={(e) => {
                        // 移动端触摸事件：立即处理，减少延迟
                        e.preventDefault();
                        e.stopPropagation();
                        const checkbox = e.currentTarget.querySelector('input[type="checkbox"]') as HTMLInputElement;
                        if (checkbox && !checkbox.disabled) {
                          checkbox.click();
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={currentLangSettings.showRomaji ?? false}
                        onChange={e => {
                          const checked = e.target.checked;
                          setDisplaySettings(prev => ({
                            ...prev,
                            [sourceLanguage]: {
                              ...currentLangSettings,
                              showRomaji: checked,
                            },
                          }));
                        }}
                      />
                      <span>显示罗马字</span>
                    </label>
                  </>
                );
              } else if (displayLang === 'en') {
                return (
                  <>
                    <div className="display-settings-title">显示内容</div>
                    <label 
                      className="display-settings-item"
                      onTouchEnd={(e) => {
                        // 移动端触摸事件：立即处理，减少延迟
                        e.preventDefault();
                        e.stopPropagation();
                        const checkbox = e.currentTarget.querySelector('input[type="checkbox"]') as HTMLInputElement;
                        if (checkbox && !checkbox.disabled) {
                          checkbox.click();
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={currentLangSettings.showIpa ?? false}
                        onChange={e => {
                          const checked = e.target.checked;
                          setDisplaySettings(prev => ({
                            ...prev,
                            [displayLang]: {
                              ...currentLangSettings,
                              showIpa: checked,
                            },
                          }));
                        }}
                      />
                      <span>显示音标 (IPA)</span>
                    </label>
                  </>
                );
              } else {
                return (
                  <>
                    <div className="display-settings-title">显示内容</div>
                    <div className="display-settings-empty">
                      当前语言（{displayLang}）暂时没有特有显示设置。
                    </div>
                  </>
                );
              }
            })()}
          </div>

          {/* 分隔线 */}
          <div className="display-settings-divider"></div>

          {/* 通用设置区域 */}
          <div className="display-settings-section">
            <div className="display-settings-title">学习功能</div>
            <label 
              className="display-settings-item"
              onTouchEnd={(e) => {
                // 移动端触摸事件：立即处理，减少延迟
                e.preventDefault();
                e.stopPropagation();
                const checkbox = e.currentTarget.querySelector('input[type="checkbox"]') as HTMLInputElement;
                if (checkbox && !checkbox.disabled) {
                  checkbox.click();
                }
              }}
            >
              <input
                type="checkbox"
                checked={subtitleControl.canWordAlign && enableWordAlignment}
                disabled={!subtitleControl.canWordAlign}
                onChange={e => {
                  const checked = e.target.checked;
                  setEnableWordAlignment(checked);
                }}
              />
              <span>
                启用词级对齐
                {!subtitleControl.canWordAlign ? '（无数据）' : ''}
              </span>
            </label>

            {/* 学习功能 与 字幕大小 之间的分隔线 */}
            <div className="display-settings-divider"></div>

            <div className="display-settings-title">字幕大小</div>
            <div className="subtitle-size-control">
              <button
                className="subtitle-size-button"
                onClick={(e) => {
                  e.stopPropagation();
                  adjustSubtitleSize(-0.1);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  adjustSubtitleSize(-0.1);
                }}
                disabled={subtitleSizeScale <= 0.8}
                title="减小"
              >
                <span className="subtitle-size-icon">A−</span>
              </button>
              <span className="subtitle-size-value">
                {Math.round(subtitleSizeScale * 100)}%
              </span>
              <button
                className="subtitle-size-button"
                onClick={(e) => {
                  e.stopPropagation();
                  adjustSubtitleSize(0.1);
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  adjustSubtitleSize(0.1);
                }}
                disabled={subtitleSizeScale >= 1.5}
                title="增大"
              >
                <span className="subtitle-size-icon">A+</span>
              </button>
              <button
                className="subtitle-size-reset"
                onClick={(e) => {
                  e.stopPropagation();
                  resetSubtitleSize();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  resetSubtitleSize();
                }}
                title="重置为默认大小"
              >
                重置
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VideoPlayerWithSubtitles;
